/**
 * Affiliate Tracking Utility
 * Handles referral link detection, cookie management, and attribution
 */

import { readAccessToken } from './accessToken';

const AFFILIATE_COOKIE_NAME = 'canira_ref';
const AFFILIATE_VISITOR_ID = 'canira_visitor_id';
const COOKIE_DURATION_DAYS = 90;

// The API lives on its own host (api.canira.io). A bare '/api/...' path goes to
// the website host, where Vercel answers 404, so these calls never reached the
// server in production.
const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Generate a unique visitor ID
 */
function generateVisitorId() {
  return 'v_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

/**
 * Get or create visitor ID
 */
export function getVisitorId() {
  let visitorId = localStorage.getItem(AFFILIATE_VISITOR_ID);
  if (!visitorId) {
    visitorId = generateVisitorId();
    localStorage.setItem(AFFILIATE_VISITOR_ID, visitorId);
  }
  return visitorId;
}

/**
 * Set a cookie with expiration
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value
 */
function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Get UTM parameters from URL
 */
function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
    utmTerm: params.get('utm_term'),
  };
}

/**
 * Get referral code from URL
 * Supports: ?ref=CODE, ?aff=CODE, ?affiliate=CODE
 */
export function getReferralCodeFromURL() {
  const params = new URLSearchParams(window.location.search);
  return params.get('ref') || params.get('aff') || params.get('affiliate');
}

/**
 * Get stored referral data from cookie
 */
export function getStoredReferral() {
  const cookieValue = getCookie(AFFILIATE_COOKIE_NAME);
  if (cookieValue) {
    try {
      return JSON.parse(cookieValue);
    } catch (e) {
      return null;
    }
  }
  return null;
}

/**
 * Store referral data in cookie
 */
function storeReferral(data) {
  setCookie(AFFILIATE_COOKIE_NAME, JSON.stringify(data), COOKIE_DURATION_DAYS);
}

/**
 * Clear referral data
 */
export function clearReferral() {
  deleteCookie(AFFILIATE_COOKIE_NAME);
}

/**
 * Track a referral click
 * Call this when a user arrives with a referral code
 */
export async function trackReferralClick(affiliateCode) {
  const visitorId = getVisitorId();
  const utmParams = getUTMParams();

  const trackingData = {
    affiliateCode,
    visitorId,
    referrerUrl: document.referrer || null,
    landingPage: window.location.href,
    ...utmParams,
  };

  try {
    const response = await fetch(`${API_BASE}/affiliates/track/click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(trackingData),
    });

    if (response.ok) {
      const result = await response.json();

      // Store referral in cookie
      storeReferral({
        affiliateCode,
        referralId: result.referralId,
        trackedAt: new Date().toISOString(),
        visitorId,
      });

      return result;
    }

    const errorData = await response.json().catch(() => ({}));
    console.error('Failed to track referral click:', response.status, errorData);
    return { success: false, error: errorData.error || `HTTP ${response.status}` };
  } catch (error) {
    console.error('Error tracking referral:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Initialize affiliate tracking
 * Call this on app startup
 */
export async function initAffiliateTracking() {
  const referralCode = getReferralCodeFromURL();

  if (referralCode) {
    // New referral - track the click
    await trackReferralClick(referralCode);

    // Clean URL by removing ref parameter (optional UX improvement)
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    url.searchParams.delete('aff');
    url.searchParams.delete('affiliate');

    // Only update URL if we removed something
    if (window.location.search !== url.search) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  return getStoredReferral();
}

/**
 * Get referral data to attach to signup/purchase
 */
export function getReferralDataForConversion() {
  const referral = getStoredReferral();
  const visitorId = getVisitorId();

  if (referral) {
    return {
      affiliateCode: referral.affiliateCode,
      referralId: referral.referralId,
      visitorId,
    };
  }

  return { visitorId };
}

/**
 * Track signup with referral attribution
 */
export async function trackSignupConversion(userId) {
  const referral = getStoredReferral();
  const visitorId = getVisitorId();

  if (!referral) {
    return null;
  }

  try {
    // The server credits the SIGNED-IN account only, so this must carry the
    // token that was just stored by the sign-up. `userId` is no longer read.
    let token = null;
    try { token = readAccessToken(); } catch { /* storage blocked */ }
    if (!token) return null;
    const response = await fetch(`${API_BASE}/affiliates/track/signup`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ visitorId }),
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    const errorData = await response.json().catch(() => ({}));
    console.error('Failed to track signup conversion:', response.status, errorData);
    return { success: false, error: errorData.error || `HTTP ${response.status}` };
  } catch (error) {
    console.error('Error tracking signup conversion:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Track purchase conversion
 * This should be called after a successful payment
 */
export async function trackPurchaseConversion(orderData) {
  const referral = getStoredReferral();
  const visitorId = getVisitorId();

  if (!referral) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/affiliates/track/conversion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        visitorId,
        ...orderData,
      }),
    });

    if (response.ok) {
      const result = await response.json();

      // Optionally clear the referral cookie after conversion
      // clearReferral();

      return result;
    }

    const errorData = await response.json().catch(() => ({}));
    console.error('Failed to track purchase conversion:', response.status, errorData);
    return { success: false, error: errorData.error || `HTTP ${response.status}` };
  } catch (error) {
    console.error('Error tracking purchase conversion:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Debug function to view current tracking state
 */
export function debugAffiliateTracking() {
}

// Export for use in browser console
if (typeof window !== 'undefined') {
  window.debugAffiliateTracking = debugAffiliateTracking;
}
